import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { type FeedbackStatus, useMarkRead, useSuggestion, useWithdraw } from '@/entities/feedback';
import { useViewer } from '@/entities/viewer';
import { formatDateTime } from '@/shared/lib/datetime';
import { Button, ConfirmDialog, ErrorState, HelpTip, Icons, LoadingState } from '@/shared/ui';

import { AttachmentList } from './AttachmentList';
import { Conversation } from './Conversation';
import { areaLabel, categoryLabel } from './labels';
import { ModerationPanel } from './ModerationPanel';
import { ReplyComposer } from './ReplyComposer';
import { StatusBadge } from './StatusBadge';
import { StatusTimeline } from './StatusTimeline';
import styles from './SuggestionDetail.module.css';

/** A returning link that keeps the list's filters (the card passed its address along). */
function backTarget(state: unknown): string {
  if (typeof state === 'object' && state !== null && 'from' in state) {
    const from = state.from;
    if (typeof from === 'string' && from.startsWith('/app/feedback')) return from;
  }
  return '/app/feedback';
}

export function SuggestionDetail({ suggestionId }: { suggestionId: string }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const query = useSuggestion(suggestionId);
  const markRead = useMarkRead();
  const withdraw = useWithdraw(suggestionId);
  const [confirming, setConfirming] = useState(false);
  const readFor = useRef<string | null>(null);
  const data = query.data;

  // Opening the conversation (not listing it) marks it read, once per visit and id.
  useEffect(() => {
    if (!data || data.unread_count === 0 || readFor.current === data.id) return;
    readFor.current = data.id;
    markRead.mutate(data.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per opened suggestion
  }, [data?.id, data?.unread_count]);

  const back = (
    <Link className={styles.back} to={backTarget(location.state)}>
      <Icons.back /> {t('feedback.detail.back')}
    </Link>
  );

  if (query.isPending) return <LoadingState />;
  if (query.isError || !data) {
    return (
      <>
        {back}
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </>
    );
  }
  const staff = data.can_moderate;
  const authorName =
    data.author?.display_name ??
    (data.own ? (viewer.status === 'authenticated' ? viewer.user.display_name : '') : '');

  return (
    <div className={styles.wrap}>
      {back}
      <header className={styles.head}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{data.title}</h1>
          <span className={styles.number}>#{data.number}</span>
        </div>
        <div className={styles.mobileStatus}>
          <StatusBadge status={data.status} animated size="lg" />
        </div>
        <div className={styles.meta}>
          <span className={styles.chip}>{categoryLabel(t, data.category)}</span>
          {data.area && <span className={styles.chip}>{areaLabel(t, data.area)}</span>}
          {staff && data.author && (
            <span>{t('feedback.card.by', { name: data.author.display_name })}</span>
          )}
          <time dateTime={data.created_at}>
            {t('feedback.detail.created', {
              when: formatDateTime(data.created_at, i18n.language, zone),
            })}
          </time>
        </div>
        {data.withdrawn && (
          <p className={styles.banner} role="status">
            {t('feedback.detail.withdrawnBanner')}
          </p>
        )}
      </header>

      <div className={styles.layout}>
        <section
          className={`${styles.card} ${styles.desc}`}
          aria-label={t('feedback.detail.description')}
        >
          <p className={styles.description}>{data.description}</p>
          <AttachmentList attachments={data.attachments} />
        </section>

        <div className={styles.side}>
          <div className={`${styles.card} ${styles.timeline}`}>
            <div className={styles.timelineHead}>
              <h2 className={styles.sectionTitle}>{t('feedback.timeline.title')}</h2>
              <HelpTip topic={t('feedback.timeline.title')} topicId="feedback-statuses" />
            </div>
            <StatusTimeline events={data.status_events} showActors={staff} timeZone={zone} />
            <p className={styles.independent}>{t('feedback.detail.independent')}</p>
          </div>
          {staff && (
            <div className={styles.moderation}>
              <ModerationPanel suggestion={data} />
            </div>
          )}
          {data.own && !data.withdrawn && (
            <Button
              className={styles.withdraw}
              variant="ghost"
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t('feedback.detail.withdraw')}
            </Button>
          )}
        </div>

        <section
          className={`${styles.section} ${styles.conversation}`}
          aria-labelledby="feedback-conversation"
        >
          <h2 id="feedback-conversation" className={styles.sectionTitle}>
            {t('feedback.conversation.title')}
          </h2>
          <Conversation
            messages={data.messages}
            staff={staff}
            authorName={authorName}
            timeZone={zone}
          />
        </section>

        {(data.can_reply || staff) && (
          <section className={`${styles.section} ${styles.reply}`} aria-labelledby="feedback-reply">
            <h2 id="feedback-reply" className={styles.sectionTitle}>
              {t('feedback.reply.title')}
            </h2>
            {data.withdrawn && staff && (
              <p className={styles.note}>{t('feedback.reply.withdrawnStaff')}</p>
            )}
            <ReplyComposer suggestionId={data.id} staff={staff} />
          </section>
        )}
      </div>
      {confirming && (
        <ConfirmDialog
          title={t('feedback.detail.withdrawTitle')}
          message={t('feedback.detail.withdrawMessage')}
          confirmLabel={t('feedback.detail.withdraw')}
          pending={withdraw.isPending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={() => {
            withdraw.mutate(undefined, {
              onSettled: () => {
                setConfirming(false);
              },
            });
          }}
        />
      )}
      <StatusBadgeAnnouncer status={data.status} />
    </div>
  );
}

/** Screen readers hear a status change that arrives while the page is open. */
function StatusBadgeAnnouncer({ status }: { status: FeedbackStatus }) {
  const { t } = useTranslation();
  return (
    <span className={styles.srOnly} role="status" aria-live="polite">
      {t('feedback.detail.statusAnnounce', { status: t(`feedback.status.${status}`) })}
    </span>
  );
}
